import {
  ChangeEvent,
  FC,
  memo,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  CustomDialog,
  CustomDialogProps,
} from '@components/Dialogs/CustomDialog';
import { useProject } from '@context/Project/ProjectProvider';
import useChangeFilterHandler from '@hooks/useChangeFilterHandler';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { TreeView } from '@mui/x-tree-view';
import { IDomain } from '@store/services/projects/types';
import { themePalette } from '@theme/muiTheme';
import {
  checkScopeFilter,
  findWideDomain,
  isSpecialScopeFilter,
  SCOPE_FILTER_NO_OPERATIONALISED,
  SCOPE_FILTER_UNASSIGNED,
} from '@utils/domains';
import TreeItem, {
  SelectHandler,
  SelectHandlerOptions,
} from '@views/Project/components/ScopeFilterModal/TreeItem';
import {
  applyScopeSelection,
  buildTree,
  findSubtreeIds,
  getDefaultExpandedForScopeTree,
  getTreeCheckState,
  ITreeItem,
} from '@views/Project/helpers';

type Props = Pick<CustomDialogProps, 'open' | 'onClose'> & {
  domains: IDomain[];
};

export type RenderTreeOptions = {
  node: ITreeItem;
  selected: Set<string>;
  onSelect: SelectHandler;
  onClick: (nodeId: string) => void;
  parentsId?: string[];
  isDisabled?: (id: string) => boolean;
  sx?: SxProps;
};

export const renderTree = ({
  node,
  selected,
  onSelect,
  onClick,
  parentsId = [],
  isDisabled,
  sx,
}: RenderTreeOptions): ReactElement => (
  <TreeItem
    key={node.id}
    nodeId={node.id}
    label={node.name}
    onSelect={({ nodeId, checked }) => onSelect({ nodeId, checked, parentsId })}
    onClick={() => onClick(node.id)}
    hasChildren={!!node.children?.length}
    checkState={getTreeCheckState(node, selected)}
    disabled={isDisabled?.(node.id)}
    sx={sx}
  >
    {node.children?.map((child) =>
      renderTree({
        node: child,
        selected,
        onSelect,
        onClick,
        parentsId: [...parentsId, node.id],
        isDisabled,
        sx,
      }),
    )}
  </TreeItem>
);

const getDomainDescendantIdsFlat = (
  domainList: IDomain[],
  rootId: string,
): string[] => {
  if (!domainList.some((d) => d.id === rootId)) {
    return [];
  }

  const out: string[] = [];
  const walk = (id: string) => {
    out.push(id);
    for (const d of domainList) {
      if (d.parentId === id) {
        walk(d.id);
      }
    }
  };
  walk(rootId);
  return out;
};

const getNodeDescendantIdsForScope = (
  treeRoots: ITreeItem[],
  domainList: IDomain[],
  nodeId: string,
): string[] => {
  if (isSpecialScopeFilter(nodeId)) {
    return [nodeId];
  }

  const fromTree = findSubtreeIds(treeRoots, nodeId);
  const fromFlat = getDomainDescendantIdsFlat(domainList, nodeId);

  return Array.from(new Set([...(fromTree ?? []), ...fromFlat]));
};

const ScopeFilterModal: FC<Props> = ({ domains, ...props }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const { toolbox, handleDomainsChange } = useProject();

  const allFilterIds = useMemo(
    () => [
      ...domains.map(({ id }) => id),
      SCOPE_FILTER_NO_OPERATIONALISED,
      SCOPE_FILTER_UNASSIGNED,
    ],
    [domains],
  );

  const treeDomains = useMemo(() => {
    return buildTree(domains);
  }, [domains]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const changeFilterHandler = useChangeFilterHandler();

  useEffect(() => {
    if (!props.open) return;

    const restored = toolbox.domains?.length ? toolbox.domains : allFilterIds;

    setSelected(restored);
    setExpanded(getDefaultExpandedForScopeTree(treeDomains, restored));
  }, [props.open, toolbox.domains, allFilterIds, treeDomains]);

  const handleAllChange = (
    _: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (checked) {
      setSelected(allFilterIds);
    } else {
      setSelected([]);
    }
  };

  const handleSelect = ({
    nodeId,
    checked,
    parentsId,
  }: SelectHandlerOptions) => {
    setSelected((ids) =>
      applyScopeSelection({
        selected: ids,
        nodeId,
        isChecked: checked,
        subtreeIds: getNodeDescendantIdsForScope(treeDomains, domains, nodeId),
        ancestorIds: parentsId,
        clearsSubtreeOnUncheck: findWideDomain(domains)?.id === nodeId,
      }),
    );

    if (checked) {
      setExpanded((ids) => Array.from(new Set([...ids, ...parentsId, nodeId])));
    }
  };

  const handleConfirm = changeFilterHandler({
    isNodeVisible: (selectedDomains, node) =>
      checkScopeFilter(
        selectedDomains,
        node?.data?.dto?.type,
        node?.data?.dto?.domainNodes,
      ),
    onSetValues: (selectedDomains) => {
      handleDomainsChange(selectedDomains);
      props.onClose?.();
    },
  });

  const handleClear = () => {
    setSelected([]);
  };

  const initialSelected = toolbox.domains?.length
    ? toolbox.domains
    : allFilterIds;
  const isNotChanged =
    selected.length === initialSelected.length &&
    selected.every((id) => initialSelected.includes(id));
  const isAllSelected =
    selected.length === allFilterIds.length &&
    allFilterIds.every((id) => selected.includes(id));
  const isSomeSelected = !isAllSelected && selected.length > 0;

  const hanleClick = (nodeId: string) => {
    if (expanded.includes(nodeId)) {
      setExpanded((ids) => ids.filter((id) => id !== nodeId));
    } else {
      setExpanded((ids) => [...ids, nodeId]);
    }
  };

  return (
    <CustomDialog
      {...props}
      title="Scope filters"
      sx={{ '& .MuiDialog-paper': { minWidth: 598, height: 595 } }}
    >
      <Divider sx={{ mt: 3 }} />

      <Stack direction="row" gap={2} alignItems="center" p="16px 24px">
        <Checkbox
          checked={isAllSelected}
          indeterminate={isSomeSelected}
          onChange={handleAllChange}
        />
        <Typography variant="h4">Select all</Typography>
      </Stack>

      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <TreeView
          expanded={expanded}
          disableSelection
          multiSelect
          selected={selected}
        >
          {treeDomains.map((item) =>
            renderTree({
              node: item,
              selected: selectedSet,
              onSelect: handleSelect,
              onClick: hanleClick,
            }),
          )}

          <TreeItem
            nodeId={SCOPE_FILTER_NO_OPERATIONALISED}
            label="No Operationalised scope"
            hasChildren={false}
            onSelect={({ nodeId, checked }) =>
              handleSelect({ nodeId, checked, parentsId: [] })
            }
            onClick={() => {}}
          />

          <TreeItem
            nodeId={SCOPE_FILTER_UNASSIGNED}
            label="Unassigned"
            hasChildren={false}
            onSelect={({ nodeId, checked }) =>
              handleSelect({ nodeId, checked, parentsId: [] })
            }
            onClick={() => {}}
          />
        </TreeView>
      </Box>

      <Stack
        p={6}
        direction="row"
        alignItems="center"
        gap={4}
        borderTop={`1px solid ${themePalette.grey[500]}`}
      >
        <Button
          sx={{ flexBasis: '100px' }}
          variant="under-text"
          onClick={handleClear}
          disabled={!selected.length}
        >
          Clear all
        </Button>

        <Button
          sx={{ flex: 1 }}
          size="large"
          variant="contained"
          onClick={() => handleConfirm(selected)}
          disabled={isNotChanged}
        >
          Confirm
        </Button>
      </Stack>
    </CustomDialog>
  );
};

export default memo(ScopeFilterModal);
